import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Utensils, Calendar, Clock, MapPin, Users, Lock, MessageCircle, X, Send, Sparkles, Wine, DollarSign, CreditCard, CheckCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getCategoryStyle = (category) => {
  const styles = {
    'Networking': { gradient: 'from-brand-gold/20 to-yellow-500/10', accent: '#FFD700', badge: 'bg-brand-gold text-black' },
    'Fine Dining': { gradient: 'from-amber-500/20 to-orange-500/10', accent: '#E6B17E', badge: 'bg-amber-500 text-black' },
    'Wine Tasting': { gradient: 'from-rose-500/20 to-red-500/10', accent: '#C41E3A', badge: 'bg-rose-500 text-white' },
    'default': { gradient: 'from-brand-gold/20 to-yellow-500/10', accent: '#FFD700', badge: 'bg-brand-gold text-black' }
  };
  return styles[category] || styles['default'];
};

export default function DinnerClub() {
  const { user, profile, isPending } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsvpLoading, setRsvpLoading] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const chatScrollRef = useRef(null);

  // Handle payment return from Stripe
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      setPaymentProcessing(true);
      pollPaymentStatus(sessionId);
      // Clear URL params
      setSearchParams({});
    } else if (payment === 'cancelled') {
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentProcessing(false);
      alert('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');
      
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        // Payment successful - create RSVP
        if (data.metadata?.event_id && data.metadata?.user_id) {
          await supabase.from('dinner_club_rsvps').insert({
            event_id: data.metadata.event_id,
            user_id: data.metadata.user_id,
            payment_session_id: sessionId
          });
        }
        setPaymentProcessing(false);
        setPaymentSuccess(true);
        setTimeout(() => setPaymentSuccess(false), 5000);
        await fetchDinnerEvents();
        return;
      } else if (data.status === 'expired') {
        setPaymentProcessing(false);
        alert('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentProcessing(false);
    }
  };

  const fetchDinnerEvents = useCallback(async () => {
    const { data: eventsData } = await supabase
      .from('dinner_club_events')
      .select('*')
      .order('event_date', { ascending: true });

    if (eventsData) {
      const eventsWithRSVPs = await Promise.all(
        eventsData.map(async (event) => {
          const { data: rsvps } = await supabase
            .from('dinner_club_rsvps')
            .select('*')
            .eq('event_id', event.id);

          return {
            ...event,
            rsvp_count: rsvps?.length || 0,
            user_rsvp: rsvps?.some(rsvp => rsvp.user_id === user?.id) || false,
          };
        })
      );
      setEvents(eventsWithRSVPs);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchDinnerEvents(); }, [fetchDinnerEvents]);

  const handleRSVP = async (eventId, currentRSVP) => {
    if (!user?.id) return;
    setRsvpLoading(eventId);
    
    try {
      if (currentRSVP) {
        // Cancel RSVP
        await supabase.from('dinner_club_rsvps').delete().eq('event_id', eventId).eq('user_id', user.id);
        await fetchDinnerEvents();
      } else {
        const event = events.find(e => e.id === eventId);
        if (!event) return;
        
        if (event.rsvp_count >= event.max_attendees) {
          alert('This dinner event is at full capacity.');
          setRsvpLoading(null);
          return;
        }
        
        // Check if event has a price (paid event)
        if (event.price && event.price > 0) {
          // Go directly to Stripe checkout with the event's price
          const response = await fetch(`${API_URL}/api/payments/dinner-club/checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event_id: event.id,
              event_title: event.title,
              amount: parseFloat(event.price),
              course_type: event.course_type || 'dinner',
              user_id: user.id,
              user_email: profile?.email || user.email,
              origin_url: window.location.origin
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to create checkout session');
          }
          
          const data = await response.json();
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
          return;
        } else {
          // Free event - direct RSVP
          await supabase.from('dinner_club_rsvps').insert({ event_id: eventId, user_id: user.id });
          await fetchDinnerEvents();
        }
      }
    } catch (error) {
      console.error('RSVP error:', error);
      alert(error.message || 'Failed to update RSVP');
    }
    setRsvpLoading(null);
  };

  const fetchChatMessages = async (eventId) => {
    const { data: messages } = await supabase
      .from('dinner_club_chat_messages')
      .select('id, user_id, message, created_at')
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });

    if (messages && messages.length > 0) {
      const userIds = [...new Set(messages.map(m => m.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, headshot_url')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      setChatMessages(messages.map(msg => ({ ...msg, profiles: profileMap.get(msg.user_id) || null })).filter(msg => msg.profiles !== null));
    } else {
      setChatMessages([]);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedEventId || !user || sendingMessage) return;
    setSendingMessage(true);
    await supabase.from('dinner_club_chat_messages').insert({ event_id: selectedEventId, user_id: user.id, message: newMessage.trim() });
    setNewMessage('');
    setTimeout(async () => { await fetchChatMessages(selectedEventId); setSendingMessage(false); }, 300);
  };

  const openChatModal = (eventId) => {
    setSelectedEventId(eventId);
    setShowChatModal(true);
    fetchChatMessages(eventId);
  };

  const formatDate = (dateString) => {
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold"></div></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Payment Processing Overlay */}
      {paymentProcessing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="glass-card rounded-2xl p-8 text-center max-w-md">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold mx-auto mb-4"></div>
            <h3 className="text-xl font-bold text-white mb-2">Processing Payment</h3>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </div>
        </div>
      )}

      {/* Payment Success Toast */}
      {paymentSuccess && (
        <div className="fixed top-4 right-4 z-50 glass-card rounded-xl p-4 flex items-center gap-3 border border-green-500/30 bg-green-500/10 animate-fade-in">
          <CheckCircle size={24} className="text-green-500" />
          <div>
            <p className="text-white font-semibold">Payment Successful!</p>
            <p className="text-gray-400 text-sm">Your RSVP has been confirmed.</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="glass-card rounded-3xl p-8 mb-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-gold/10 to-transparent" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Wine size={20} className="text-brand-gold" />
            <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider">Premium Dining</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Dinner Club</h1>
          <p className="text-gray-400 leading-relaxed max-w-2xl mb-6">
            Exclusive dining experiences featuring complimentary wine, multi-course meals, and intimate private dining with fellow members.
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <Wine size={16} className="text-brand-gold" />
              <span className="text-gray-300">Complimentary Wine</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <Utensils size={16} className="text-brand-gold" />
              <span className="text-gray-300">Multi-Course Meal</span>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="glass-card rounded-2xl p-5 mb-8 border border-pink-500/20 bg-gradient-to-r from-pink-500/5 to-transparent">
        <p className="text-pink-300 text-sm text-center font-medium">
          ⚠️ Important: Do not RSVP if you do not plan to attend. All reservations are non-refundable and non-transferable. Once RSVP is full, it's FULL. Guest must RSVP by creating a profile and going through the membership application process.
        </p>
      </div>

      {events.length === 0 ? (
        <div className="glass-card rounded-3xl p-12 text-center">
          <Utensils size={64} className="mx-auto text-brand-gold mb-6" />
          <h3 className="text-2xl font-bold gradient-text mb-3">No Upcoming Dinners</h3>
          <p className="text-gray-400">Check back soon for the next exclusive dinner event</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="dinner-events-grid">
          {events.map((event, index) => {
            const style = getCategoryStyle(event.category || 'Networking');
            return (
              <div 
                key={event.id} 
                className={`glass-card rounded-2xl overflow-hidden card-hover animate-fadeIn bg-gradient-to-br ${style.gradient}`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`dinner-card-${event.id}`}
              >
                {event.image_url && (
                  <div className="relative bg-black/30">
                    <img src={event.image_url} alt={event.title} className="w-full h-auto max-h-64 object-contain mx-auto" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                  </div>
                )}
                <div className="p-6">
                  <span className={`inline-block px-4 py-1.5 rounded-lg text-xs font-bold mb-4 ${style.badge}`}>
                    {event.category || 'Networking'}
                  </span>
                  <h3 className="text-xl font-bold text-white mb-4">{event.title}</h3>
                  
                  {/* Price Badge */}
                  <div className="flex items-center justify-center gap-2 py-3 mb-5 rounded-xl bg-black/20 border border-brand-gold/20">
                    <DollarSign size={20} style={{ color: style.accent }} />
                    <span className="text-2xl font-bold text-white">{event.price?.toFixed(2)}</span>
                    <span className="text-gray-400 text-sm">per person</span>
                  </div>

                  {/* Show attending count only to signed-in members */}
                  {user && (
                    <div className="flex items-center justify-center gap-2 mb-4 text-sm">
                      <Users size={16} style={{ color: style.accent }} />
                      <span className="text-gray-300">
                        <span className="font-bold text-white">{event.rsvp_count}</span> / {event.max_attendees} attending
                      </span>
                    </div>
                  )}

                  <div className="space-y-3 mb-5">
                    <div className="flex items-center text-gray-300 text-sm">
                      <Calendar size={16} style={{ color: style.accent }} className="mr-3" />
                      {formatDate(event.event_date)}
                    </div>
                    <div className="flex items-center text-gray-300 text-sm">
                      <Clock size={16} style={{ color: style.accent }} className="mr-3" />
                      {event.event_time}
                    </div>
                    <div className="flex items-center text-gray-300 text-sm">
                      <MapPin size={16} style={{ color: style.accent }} className="mr-3" />
                      {event.venue}
                    </div>
                  </div>

                  <p className="text-gray-400 text-sm mb-6">{event.description}</p>

                  {!user ? (
                    <button 
                      onClick={() => navigate('/auth')} 
                      className="w-full py-4 rounded-xl bg-brand-gold/10 border border-brand-gold/20 text-brand-gold text-sm font-medium flex items-center justify-center gap-2"
                      data-testid={`signin-prompt-${event.id}`}
                    >
                      <Lock size={16} /> Sign in to RSVP
                    </button>
                  ) : profile && !isPending ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => handleRSVP(event.id, event.user_rsvp)}
                        disabled={rsvpLoading === event.id || (event.rsvp_count >= event.max_attendees && !event.user_rsvp)}
                        className={`w-full py-4 rounded-xl text-base font-bold transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 ${
                          event.user_rsvp 
                            ? 'btn-secondary' 
                            : 'btn-primary'
                        }`}
                        data-testid={`rsvp-btn-${event.id}`}
                      >
                        {rsvpLoading === event.id ? (
                          'Processing...'
                        ) : event.user_rsvp ? (
                          'Cancel RSVP'
                        ) : event.rsvp_count >= event.max_attendees ? (
                          'Event Full'
                        ) : event.price && event.price > 0 ? (
                          <>
                            <CreditCard size={18} />
                            Pay ${parseFloat(event.price).toFixed(0)} & RSVP
                          </>
                        ) : (
                          'RSVP Now (Free)'
                        )}
                      </button>
                      <button 
                        onClick={() => openChatModal(event.id)} 
                        className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-gray-300 font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
                        data-testid={`chat-btn-${event.id}`}
                      >
                        <MessageCircle size={18} /> Event Chat
                      </button>
                    </div>
                  ) : user && isPending ? (
                    <div className="flex items-center justify-center gap-2 py-4 rounded-xl bg-brand-gold/10 border border-brand-gold/20">
                      <Lock size={18} className="text-brand-gold" />
                      <span className="text-brand-gold font-semibold">RSVP after approval</span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal && selectedEventId && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={() => setShowChatModal(false)} data-testid="dinner-chat-modal">
          <div className="bg-brand-dark-light rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h3 className="text-xl font-bold gradient-text">Event Chat</h3>
              <button onClick={() => setShowChatModal(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                <X size={24} className="text-gray-400" />
              </button>
            </div>
            <div ref={chatScrollRef} className="flex-1 p-6 overflow-y-auto space-y-4 max-h-80">
              {chatMessages.map((msg) => (
                <div key={msg.id} className={msg.user_id === user?.id ? 'ml-auto' : ''}>
                  {msg.user_id !== user?.id && (
                    <div className="flex items-center gap-2 mb-1">
                      {msg.profiles?.headshot_url ? <img src={msg.profiles.headshot_url} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-brand-gold/20" />}
                      <span className="text-xs text-brand-gold font-semibold">{msg.profiles?.full_name}</span>
                    </div>
                  )}
                  <div className={`max-w-xs px-4 py-3 rounded-2xl ${msg.user_id === user?.id ? 'bg-gradient-to-r from-brand-gold to-yellow-500 text-black ml-auto' : 'bg-white/5 text-white border border-white/10'}`}>
                    <p className="text-sm">{msg.message}</p>
                    <p className={`text-xs mt-1 ${msg.user_id === user?.id ? 'text-black/60' : 'text-gray-500'}`}>{new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
              {chatMessages.length === 0 && <p className="text-gray-400 text-center py-8">No messages yet. Start the conversation!</p>}
            </div>
            <div className="p-4 border-t border-brand-gold/20 flex gap-3">
              <input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()} placeholder="Type a message..." className="flex-1 px-4 py-3 bg-black/30 rounded-xl text-white placeholder-gray-500 focus:outline-none border border-brand-gold/20 focus:border-brand-gold transition-colors" />
              <button onClick={sendChatMessage} disabled={!newMessage.trim() || sendingMessage} className="btn-primary w-12 h-12 rounded-xl flex items-center justify-center disabled:opacity-40"><Send size={18} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
